import React from "react";
import { Link } from "react-router-dom";
import {
  Database,
  CheckCircle2,
  AlertCircle,
  Users,
  Shield,
  Key,
  PieChart,
  ArrowRight,
  Lock,
  Copy,
  Check,
  Tag,
  Radio,
} from "lucide-react";
import { useDashboard } from "../context/DashboardContext";
import "./Overview.css";

const Overview = () => {
  const {
    user,
    role,
    roleLabel,
    roleBadgeClass,
    canCreateKeys,
    canManageUsers,
    creatableRoles,
    rawLicenses,
    stats,
    copiedKey,
    handleCopyKey,
    formatISTDateTime,
    isExpired,
  } = useDashboard();

  const total = stats.totalLicenses || 1;
  const pct7 = Math.round((stats.plan7 / total) * 100);
  const pct15 = Math.round((stats.plan15 / total) * 100);
  const pct30 = Math.round((stats.plan30 / total) * 100);

  // Take latest 5 licenses for quick preview
  const recentLicenses = [...rawLicenses]
    .sort((a, b) => new Date(b.genTime || 0) - new Date(a.genTime || 0))
    .slice(0, 5);

  return (
    <div className="bf-overview-container">
      {/* Welcome Banner */}
      <section className="bf-overview-header">
        <div className="bf-overview-header-left">
          <h1 className="bf-overview-title">
            <span>OPERATOR CONSOLE //</span>
            <span style={{ color: "var(--green-0)" }}>{user.username || "COMMAND"}</span>
          </h1>
          <p className="bf-overview-subtitle">
            Authenticated as <strong style={{ color: "var(--text-0)" }}>{roleLabel}</strong>. Cluster node synchronized with Cloudflare KV storage.
          </p>
        </div>

        <div className="bf-header-actions">
          {canCreateKeys && (
            <Link to="/dashboard/licenses" className="bf-btn-nav-action primary">
              <Key size={15} />
              <span>ISSUE KEY</span>
            </Link>
          )}

          {canManageUsers && (
            <Link to="/dashboard/controls" className="bf-btn-nav-action">
              <Users size={15} />
              <span>PROVISION OPERATOR</span>
            </Link>
          )}
        </div>
      </section>

      {/* Top HUD Metrics Grid */}
      <section className="bf-metrics-grid">
        <div className="bf-metric-card highlight">
          <div className="bf-metric-info">
            <span className="tech-label">TOTAL LICENSES</span>
            <span className="bf-metric-value">{stats.totalLicenses}</span>
          </div>
          <div className="bf-metric-icon">
            <Database size={20} />
          </div>
        </div>

        <div className="bf-metric-card">
          <div className="bf-metric-info">
            <span className="tech-label">ACTIVE / VALID</span>
            <span className="bf-metric-value" style={{ color: "var(--green-0)" }}>
              {stats.activeCount}
            </span>
          </div>
          <div className="bf-metric-icon" style={{ color: "var(--green-0)" }}>
            <CheckCircle2 size={20} />
          </div>
        </div>

        <div className="bf-metric-card">
          <div className="bf-metric-info">
            <span className="tech-label">EXPIRED KEYS</span>
            <span className="bf-metric-value" style={{ color: "var(--red)" }}>
              {stats.expiredCount}
            </span>
          </div>
          <div className="bf-metric-icon" style={{ color: "var(--red)" }}>
            <AlertCircle size={20} />
          </div>
        </div>

        {canManageUsers ? (
          <div className="bf-metric-card">
            <div className="bf-metric-info">
              <span className="tech-label">DOWNLINE OPERATORS</span>
              <span className="bf-metric-value" style={{ color: "var(--blue)" }}>
                {stats.totalUsers}
              </span>
            </div>
            <div className="bf-metric-icon" style={{ color: "var(--blue)" }}>
              <Users size={20} />
            </div>
          </div>
        ) : (
          <div className="bf-metric-card">
            <div className="bf-metric-info">
              <span className="tech-label">CONSUMED STATUS</span>
              <span className="bf-metric-value" style={{ color: "var(--amber)" }}>
                {stats.usedCount}
              </span>
            </div>
            <div className="bf-metric-icon" style={{ color: "var(--amber)" }}>
              <CheckCircle2 size={20} />
            </div>
          </div>
        )}
      </section>

      {/* Analytics & System HUD Breakdown Grid */}
      <section className="bf-analytics-grid">
        {/* Plan Duration Distribution */}
        <div className="bf-panel">
          <div className="bf-panel-header">
            <div className="bf-panel-title">
              <PieChart size={16} color="var(--green-0)" />
              <span>PLAN DURATION BREAKDOWN</span>
            </div>
            <span className="tech-badge neutral">
              {stats.totalLicenses} TOTAL
            </span>
          </div>

          <div className="bf-progress-list">
            <div className="bf-progress-item">
              <div className="bf-progress-label-row">
                <span>7 DAYS [EVALUATION]</span>
                <span>{stats.plan7} keys ({pct7}%)</span>
              </div>
              <div className="bf-progress-track">
                <div className="bf-progress-fill green" style={{ width: `${pct7}%` }} />
              </div>
            </div>

            <div className="bf-progress-item">
              <div className="bf-progress-label-row">
                <span>15 DAYS [STANDARD]</span>
                <span>{stats.plan15} keys ({pct15}%)</span>
              </div>
              <div className="bf-progress-track">
                <div className="bf-progress-fill blue" style={{ width: `${pct15}%` }} />
              </div>
            </div>

            <div className="bf-progress-item">
              <div className="bf-progress-label-row">
                <span>30 DAYS [FULL MONTH]</span>
                <span>{stats.plan30} keys ({pct30}%)</span>
              </div>
              <div className="bf-progress-track">
                <div className="bf-progress-fill amber" style={{ width: `${pct30}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* Security Clearance Specs */}
        <div className="bf-panel">
          <div className="bf-panel-header">
            <div className="bf-panel-title">
              <Shield size={16} color="var(--blue)" />
              <span>OPERATIONAL CLEARANCE SPECS</span>
            </div>
            <span className={`tech-badge ${roleBadgeClass}`}>{roleLabel}</span>
          </div>

          <div className="bf-specs-list">
            <div className="bf-spec-row">
              <span className="bf-spec-label">
                <Radio size={12} color="var(--green-0)" />
                CLEARANCE LEVEL
              </span>
              <span className="bf-spec-value">{`${role.toUpperCase()} // TIER_ACTIVE`}</span>
            </div>

            <div className="bf-spec-row">
              <span className="bf-spec-label">
                <Key size={12} />
                KEY MINTING
              </span>
              <span
                className="bf-spec-value"
                style={{ color: canCreateKeys ? "var(--green-0)" : "var(--amber)" }}
              >
                {canCreateKeys ? "AUTHORIZED (DIRECT)" : "DELEGATED (SELLER)"}
              </span>
            </div>

            <div className="bf-spec-row">
              <span className="bf-spec-label">
                <Users size={12} />
                DOWNLINE ACCESS
              </span>
              <span className="bf-spec-value">
                {canManageUsers
                  ? creatableRoles.map((r) => r.toUpperCase()).join(", ")
                  : "NONE (TERMINAL NODE)"}
              </span>
            </div>

            <div className="bf-spec-row">
              <span className="bf-spec-label">
                <Lock size={12} />
                SESSION SECURITY
              </span>
              <span className="bf-spec-value" style={{ color: "var(--green-0)" }}>
                HMAC-SHA256 ENCRYPTED
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Licenses Preview Table */}
      <section className="bf-panel bf-recent-activity-card">
        <div className="bf-panel-header">
          <div className="bf-panel-title">
            <Database size={16} color="var(--green-0)" />
            <span>RECENT KEY PROVISIONS (LATEST 5)</span>
          </div>
          <Link to="/dashboard/licenses" className="bf-link-view-all">
            <span>EXPLORE FULL INVENTORY</span>
            <ArrowRight size={14} />
          </Link>
        </div>

        {recentLicenses.length === 0 ? (
          <div className="bf-empty-state">
            <Database size={32} strokeWidth={1.5} color="var(--text-3)" />
            <span>NO LICENSE RECORDS RECENTLY MINTED</span>
          </div>
        ) : (
          <div className="bf-table-container">
            <table className="bf-data-table">
              <thead>
                <tr>
                  <th>RECIPIENT</th>
                  <th>LICENSE KEY</th>
                  <th>PLAN</th>
                  <th>EXPIRY (IST)</th>
                  <th>STATUS</th>
                  <th>PAYMENT</th>
                  <th>ISSUED BY</th>
                </tr>
              </thead>
              <tbody>
                {recentLicenses.map((item) => {
                  const expired = isExpired(item.expiryDate);
                  const isCopied = copiedKey === item.licenseKey;

                  return (
                    <tr key={item._id} className={expired ? "row-expired" : ""}>
                      <td>
                        <span style={{ fontWeight: 600, color: "var(--text-0)" }}>
                          {item.name}
                        </span>
                      </td>
                      <td>
                        <div className="bf-key-cell">
                          <span>{item.licenseKey}</span>
                          <button
                            className={`bf-copy-btn ${isCopied ? "bf-copied-badge" : ""}`}
                            onClick={() => handleCopyKey(item.licenseKey)}
                            title={isCopied ? "Copied to clipboard!" : "Copy Key"}
                          >
                            {isCopied ? <Check size={13} /> : <Copy size={13} />}
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className="tech-badge neutral">
                          {item.plan ? `${item.plan}D` : "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className="font-mono"
                          style={{
                            fontSize: "12px",
                            color: expired ? "var(--red)" : "var(--text-1)",
                          }}
                        >
                          {formatISTDateTime(item.expiryDate)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`tech-badge ${item.hasUsed ? "amber" : "green"}`}
                        >
                          {item.hasUsed ? "CONSUMED" : "ACTIVE"}
                        </span>
                      </td>
                      <td>
                        <span className="tech-badge neutral">
                          <Tag size={10} />
                          {item.payment || "NONE"}
                        </span>
                      </td>
                      <td>
                        <span className="font-mono" style={{ fontSize: "12px", color: "var(--text-2)" }}>
                          {item.genBy}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Overview;
